import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  IconButton,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Checkbox,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Pencil, Trash, Gear } from '@phosphor-icons/react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format, isPast, isToday, isTomorrow, differenceInDays } from 'date-fns';
import { enGB } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import useThemeStyles from '../../theme/useThemeStyles';

function TodoList() {
  const { wedding } = useAuth();
  const styles = useThemeStyles();
  const theme = useTheme();
  const _isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [todos, setTodos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [guests, setGuests] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTodoTitle, setNewTodoTitle] = useState('');
  
  // Task drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    notes: '',
    categoryId: '',
    categoryName: '',
    dueDate: null,
    assignees: [],
    subtasks: [],
    reminder: { enabled: true, daysBefore: 3, onDueDate: true },
    priority: 'medium'
  });

  // UI state
  const [recommendationsExpanded, setRecommendationsExpanded] = useState(true);
  const [_completedExpanded, _setCompletedExpanded] = useState(false);
  
  // Category management modal state
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Sorting state
  const [sortField, setSortField] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (wedding?._id) {
      fetchTodos();
      fetchCategories();
      fetchGuests();
      fetchRecommendations();
    }
  }, [wedding?._id]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/todos');
      setTodos(response.data || []);
    } catch (error) {
      setError('Failed to fetch tasks: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/todos/categories');
      // Backend returns { categories: [...] }
      const categoriesData = response.data?.categories || response.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      setCategories([]); // Set empty array on error
    }
  };

  // Add new category
  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await api.post('/api/todos/categories', { name: newCategoryName.trim() });
      setNewCategoryName('');
      await fetchCategories();
    } catch (error) {
      setError('Failed to add category: ' + (error.response?.data?.message || error.message));
    }
  };

  // Delete category
  const deleteCategory = async (categoryId) => {
    try {
      await api.delete(`/api/todos/categories/${categoryId}`);
      await fetchCategories();
      await fetchTodos(); // Refresh todos in case any were reassigned
    } catch (error) {
      setError('Failed to delete category: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await api.get('/api/guests');
      setGuests(response.data || []);
    } catch (error) {}
  };

  const fetchRecommendations = async () => {
    try {
      const response = await api.get('/api/todos/recommendations');
      setRecommendations(response.data?.recommendations || []);
    } catch (error) {}
  };

  // Get assignees for a task (supports both new assignees array and legacy assignee object)
  const getTaskAssignees = (task) => {
    if (task.assignees && task.assignees.length > 0) {
      return task.assignees;
    }
    if (task.assignee && task.assignee.guestId) {
      return [task.assignee];
    }
    return [];
  };

  // Resolve assignee display name (from stored displayName or populated guestId)
  const getAssigneeDisplayName = (assignee) =>
    assignee?.displayName || assignee?.guestId?.name || '';

  // Resolve assignee guest id for matching (handles both ObjectId and populated object)
  const getAssigneeGuestId = (assignee) =>
    assignee?.guestId?._id || assignee?.guestId;

  // Get category name
  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'Other';
    const cat = categories.find(c => (c._id || c.id) === categoryId);
    return cat?.name || 'Other';
  };

  // Open task drawer
  const openTaskDrawer = (task = null) => {
    if (task) {
      setSelectedTask(task);
      const taskAssignees = getTaskAssignees(task);
      setTaskForm({
        title: task.title || '',
        notes: task.notes || task.description || '',
        categoryId: task.categoryId || '',
        categoryName: task.categoryName || getCategoryName(task.categoryId),
        dueDate: task.dueDate ? new Date(task.dueDate) : null,
        assignees: taskAssignees,
        subtasks: task.subtasks || [],
        reminder: task.reminder || { enabled: true, daysBefore: 3, onDueDate: true },
        priority: task.priority || 'medium'
      });
      } else {
      setSelectedTask(null);
      setTaskForm({
        title: '',
        notes: '',
        categoryId: '',
        categoryName: '',
        dueDate: null,
        assignees: [],
        subtasks: [],
        reminder: { enabled: true, daysBefore: 3, onDueDate: true },
        priority: 'medium'
      });
    }
    setDrawerOpen(true);
  };

  const closeTaskDrawer = () => {
    setDrawerOpen(false);
    setSelectedTask(null);
  };

  // Save task
  const saveTask = async () => {
    try {
      if (!taskForm.title.trim()) {
        setError('Task title is required');
        return;
      }

      const taskData = {
        ...taskForm,
        title: taskForm.title.trim(),
        notes: taskForm.notes.trim(),
        dueDate: taskForm.dueDate ? taskForm.dueDate.toISOString() : null,
        assignees: taskForm.assignees || []
      };

      if (selectedTask) {
        await api.put(`/api/todos/${selectedTask._id}`, taskData);
      } else {
        await api.post('/api/todos', taskData);
      }

      await fetchTodos();
      closeTaskDrawer();
      setError('');
    } catch (error) {
      setError('Failed to save task: ' + (error.response?.data?.message || error.message));
    }
  };

  // Toggle task completion
  const toggleTask = async (taskId) => {
    try {
      const task = todos.find(t => t._id === taskId);
      if (!task) return;

      if (task.status === 'completed' || task.completed) {
        await api.post(`/api/todos/${taskId}/uncomplete`);
        } else {
        await api.post(`/api/todos/${taskId}/complete`);
        }
      await fetchTodos();
    } catch (error) {
      setError('Failed to update task');
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/api/todos/${taskId}`);
      await fetchTodos();
      if (selectedTask?._id === taskId) {
        closeTaskDrawer();
      }
    } catch (error) {
      setError('Failed to delete task');
    }
  };

  // Add quick task
  const addQuickTask = async () => {
    if (!newTodoTitle.trim()) return;
    try {
      await api.post('/api/todos', { title: newTodoTitle.trim() });
      setNewTodoTitle('');
      await fetchTodos();
    } catch (error) {
      setError('Failed to add task');
    }
  };

  // Add recommended task
  const addRecommendedTask = async (recommendation) => {
    try {
      await api.post('/api/todos', {
        title: recommendation.title,
        categoryId: recommendation.categoryId,
        priority: recommendation.priority,
        dueDate: recommendation.dueDate,
        aiSuggested: true
      });
      await fetchTodos();
      await fetchRecommendations();
    } catch (error) {
      setError('Failed to add recommended task');
    }
  };

  // Update subtask
  const updateSubtask = (index, field, value) => {
    const newSubtasks = [...taskForm.subtasks];
    if (field === 'title') {
      newSubtasks[index] = { ...newSubtasks[index], title: value };
    } else if (field === 'completed') {
      newSubtasks[index] = { ...newSubtasks[index], completed: value };
    }
    setTaskForm({ ...taskForm, subtasks: newSubtasks });
  };

  // Add subtask
  const addSubtask = () => {
    setTaskForm({
      ...taskForm,
      subtasks: [...taskForm.subtasks, { title: '', completed: false }]
    });
  };

  // Remove subtask
  const removeSubtask = (index) => {
    const newSubtasks = taskForm.subtasks.filter((_, i) => i !== index);
    setTaskForm({ ...taskForm, subtasks: newSubtasks });
  };

  // Get due date chip color (only for overdue/today/tomorrow)
  const getDueDateChipColor = (dueDate) => {
    if (!dueDate) return null; // No color for future dates
    const date = new Date(dueDate);
    if (isPast(date) && !isToday(date)) return 'error';
    if (isToday(date)) return 'warning';
    if (isTomorrow(date)) return 'warning';
    return null; // Future dates - no color, just grey text
  };

  // Format due date (British format)
  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isPast(date)) {
      const daysPast = differenceInDays(new Date(), date);
      return `${daysPast} day${daysPast > 1 ? 's' : ''} ago`;
    }
    return format(date, 'dd/MM/yyyy', { locale: enGB });
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort todos
  const sortedTodos = useMemo(() => {
    const todosToSort = [...todos];
    return todosToSort.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'dueDate':
          // Items without due dates always come last
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1; // a comes after b
          if (!b.dueDate) return -1; // b comes after a
          aValue = new Date(a.dueDate).getTime();
          bValue = new Date(b.dueDate).getTime();
          break;
        case 'category':
          // Uncategorized items always come last
          const aCat = (a.categoryName || 'Uncategorized').toLowerCase();
          const bCat = (b.categoryName || 'Uncategorized').toLowerCase();
          if (aCat === 'uncategorized' && bCat === 'uncategorized') return 0;
          if (aCat === 'uncategorized') return 1; // a comes after b
          if (bCat === 'uncategorized') return -1; // b comes after a
          aValue = aCat;
          bValue = bCat;
          break;
        case 'assignee':
          const aAssignees = a.assignees || (a.assignee ? [a.assignee] : []);
          const bAssignees = b.assignees || (b.assignee ? [b.assignee] : []);
          // Unassigned items always come last
          if (aAssignees.length === 0 && bAssignees.length === 0) return 0;
          if (aAssignees.length === 0) return 1; // a comes after b
          if (bAssignees.length === 0) return -1; // b comes after a
          aValue = (aAssignees[0].displayName || '').toLowerCase();
          bValue = (bAssignees[0].displayName || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [todos, sortField, sortDirection]);

  const activeTodos = sortedTodos.filter(t => t.status !== 'completed' && t.status !== 'archived');
  const completedTodos = sortedTodos.filter(t => t.status === 'completed');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
    <Box sx={styles.container.page}>
      <Box sx={{
        ...styles.patterns.pageHeader,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
      }}>
        <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, textAlign: { xs: 'center', sm: 'left' } }}>
          Wedding Planning Tasks
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Gear size={20} />}
          onClick={() => setCategoryModalOpen(true)}
          size="small"
          sx={{ alignSelf: { xs: 'stretch', sm: 'inherit' }, minWidth: { xs: 'auto', sm: undefined } }}
        >
          Manage Categories
        </Button>
      </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Quick Add */}
        <Card sx={{ bgcolor: 'white', mb: 3, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={styles.spacing.compact}>
              <Box sx={styles.layout.flexRow} gap={1}>
              <TextField
                fullWidth
                size="small"
                value={newTodoTitle}
                onChange={(e) => setNewTodoTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addQuickTask()}
                placeholder="Add a new task..."
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={addQuickTask}
                startIcon={<AddIcon />}
                disabled={!newTodoTitle.trim()}
              >
                Add
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <Accordion
            expanded={recommendationsExpanded}
            onChange={() => setRecommendationsExpanded(!recommendationsExpanded)}
              sx={{
              mb: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.5)', 
              backdropFilter: 'blur(10px)',
              boxShadow: 'none', 
              border: '1px solid rgba(0, 0, 0, 0.08)' 
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Recommended Tasks ({recommendations.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {recommendations.map((rec, idx) => (
                    <Card key={idx} sx={{ bgcolor: 'rgba(255, 243, 224, 0.4)', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <CardContent sx={styles.spacing.compact}>
                <Box sx={styles.layout.flexBetween}>
                    <Box>
                  <Typography variant="body1" fontWeight="medium">
                            {rec.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                            {rec.reason}
                  </Typography>
                    </Box>
                  <Button
                    size="small"
                    variant="outlined"
                          onClick={() => addRecommendedTask(rec)}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
              </CardContent>
            </Card>
                ))}
                  </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Incomplete Tasks */}
        <Typography variant="h6" sx={styles.text.sectionTitle} mt={3}>
          Incomplete Tasks ({activeTodos.length})
        </Typography>

        {loading ? (
          <Box sx={styles.patterns.loadingState}>
            <CircularProgress />
                </Box>
        ) : activeTodos.length === 0 ? (
          <Card sx={{ bgcolor: 'white', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)', mt: 2 }}>
            <CardContent sx={styles.patterns.emptyState}>
              <Typography variant="h6" color="text.secondary">
                No incomplete tasks
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Add a task above or check out the recommended tasks!
              </Typography>
              </CardContent>
            </Card>
        ) : (
          <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(10px)', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)', mt: 2 }}>
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                    <TableCell padding="checkbox" width="50"></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'title'}
                        direction={sortField === 'title' ? sortDirection : 'asc'}
                        onClick={() => handleSort('title')}
                      >
                        Task Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'assignee'}
                        direction={sortField === 'assignee' ? sortDirection : 'asc'}
                        onClick={() => handleSort('assignee')}
                      >
                        Assignees
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'dueDate'}
                        direction={sortField === 'dueDate' ? sortDirection : 'asc'}
                        onClick={() => handleSort('dueDate')}
                      >
                        Due Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortField === 'category'}
                        direction={sortField === 'category' ? sortDirection : 'asc'}
                        onClick={() => handleSort('category')}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" width="100" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeTodos.map(task => {
                    const taskAssignees = getTaskAssignees(task);
                    return (
                      <TableRow
                        key={task._id}
              sx={{
                          cursor: 'pointer',
                          bgcolor: 'white',
                          borderLeft: '3px solid transparent',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderLeftColor: 'primary.main'
                          },
                          '&:last-child td': {
                            borderBottom: 'none'
                          }
                        }}
                        onClick={(e) => {
                          // Only open drawer if click is not on checkbox or icon buttons
                          if (!e.target.closest('input[type="checkbox"]') && 
                              !e.target.closest('button')) {
                            openTaskDrawer(task);
                          }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={task.completed || task.status === 'completed'}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleTask(task._id);
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {task.title}
                            </Typography>
                            {task.subtasks?.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {task.subtasks.filter(st => st.completed).length}/{task.subtasks.length} subtasks
                              </Typography>
                            )}
                  </Box>
                        </TableCell>
                        <TableCell>
                          {taskAssignees.length > 0 ? (
                            <AvatarGroup 
                              max={3} 
              sx={{
                                flexDirection: 'row',
                                '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem' },
                                justifyContent: 'flex-start'
                              }}
                            >
                              {taskAssignees.map((assignee, idx) => (
                                <Avatar
                                  key={idx}
                                  sx={{ bgcolor: 'primary.main' }}
                                  title={getAssigneeDisplayName(assignee)}
                                >
                                  {getAssigneeDisplayName(assignee).charAt(0).toUpperCase() || '?'}
                                </Avatar>
                              ))}
                            </AvatarGroup>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Unassigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {task.dueDate ? (
                            (() => {
                              const chipColor = getDueDateChipColor(task.dueDate);
                              const date = new Date(task.dueDate);
                              const isFuture = !isPast(date) && !isToday(date) && !isTomorrow(date);
                              
                              if (isFuture) {
                                // Future dates: grey text on white
                                return (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {formatDueDate(task.dueDate)}
                                  </Typography>
                                );
                              } else {
                                // Overdue/today/tomorrow: colored chip
                                return (
                                  <Chip
                                    label={formatDueDate(task.dueDate)}
                                    size="small"
                                    color={chipColor}
              sx={{
                                      height: 26,
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      color: 'white'
                                    }}
                                  />
                                );
                              }
                            })()
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No due date
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={task.categoryName || 'Uncategorized'}
                            size="small"
                sx={{
                              height: 26,
                              fontSize: '0.75rem',
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTaskDrawer(task);
                              }}
                            >
                              <Pencil size={18} />
                            </IconButton>
              <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteTask(task._id);
                              }}
                            >
                              <Trash size={18} />
              </IconButton>
                </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </Card>
          )}

        {/* Completed Tasks */}
        {completedTodos.length > 0 && (
          <>
            <Typography variant="h6" sx={styles.text.sectionTitle} mt={3}>
              Completed Tasks ({completedTodos.length})
            </Typography>
            <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(10px)', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)', mt: 2 }}>
              <TableContainer>
                <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                      <TableCell padding="checkbox" width="50"></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortField === 'title'}
                          direction={sortField === 'title' ? sortDirection : 'asc'}
                          onClick={() => handleSort('title')}
                        >
                          Task Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortField === 'assignee'}
                          direction={sortField === 'assignee' ? sortDirection : 'asc'}
                          onClick={() => handleSort('assignee')}
                        >
                          Assignees
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortField === 'dueDate'}
                          direction={sortField === 'dueDate' ? sortDirection : 'asc'}
                          onClick={() => handleSort('dueDate')}
                        >
                          Due Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortField === 'category'}
                          direction={sortField === 'category' ? sortDirection : 'asc'}
                          onClick={() => handleSort('category')}
                        >
                          Category
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" width="100" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completedTodos.map(task => {
                      const taskAssignees = getTaskAssignees(task);
                      return (
                        <TableRow
                          key={task._id}
                  sx={{
                            cursor: 'pointer',
                            bgcolor: 'white',
                    opacity: 0.7,
                            '&:hover': {
                              opacity: 0.9,
                              bgcolor: 'action.hover'
                            },
                            '&:last-child td': {
                              borderBottom: 'none'
                            }
                          }}
                          onClick={(e) => {
                            // Only open drawer if click is not on checkbox or icon buttons
                            if (!e.target.closest('input[type="checkbox"]') && 
                                !e.target.closest('button')) {
                              openTaskDrawer(task);
                            }
                          }}
                        >
                          <TableCell padding="checkbox">
                    <Checkbox
                              checked={true}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleTask(task._id);
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                        <Typography
                              variant="body2"
                              sx={{ textDecoration: 'line-through', color: 'text.secondary', fontWeight: 500 }}
                            >
                              {task.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {taskAssignees.length > 0 ? (
                              <AvatarGroup 
                                max={3} 
                    sx={{
                                  flexDirection: 'row',
                                  '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem', opacity: 0.7 },
                                  justifyContent: 'flex-start'
                                }}
                              >
                                {taskAssignees.map((assignee, idx) => (
                                  <Avatar
                                    key={idx}
                                    sx={{ bgcolor: 'primary.main' }}
                                    title={getAssigneeDisplayName(assignee)}
                                  >
                                    {getAssigneeDisplayName(assignee).charAt(0).toUpperCase() || '?'}
                                  </Avatar>
                                ))}
                              </AvatarGroup>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Unassigned
                        </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {task.dueDate ? (
                              (() => {
                                const chipColor = getDueDateChipColor(task.dueDate);
                                const date = new Date(task.dueDate);
                                const isFuture = !isPast(date) && !isToday(date) && !isTomorrow(date);
                                
                                if (isFuture) {
                                  // Future dates: grey text on white
                                  return (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, opacity: 0.7 }}>
                                      {formatDueDate(task.dueDate)}
                                    </Typography>
                                  );
                                } else {
                                  // Overdue/today/tomorrow: colored chip
                                  return (
                                    <Chip
                                      label={formatDueDate(task.dueDate)}
                                      size="small"
                                      color={chipColor}
                                      sx={{
                                        height: 26,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        opacity: 0.7
                                      }}
                                    />
                                  );
                                }
                              })()
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No due date
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={task.categoryName || 'Uncategorized'}
                              size="small"
              sx={{
                                height: 26,
                                fontSize: '0.75rem',
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontWeight: 600,
                                opacity: 0.7
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTaskDrawer(task);
                                }}
                              >
                                <Pencil size={18} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteTask(task._id);
                                }}
                              >
                                <Trash size={18} />
                  </IconButton>
                    </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
                </Card>
          </>
        )}

        {/* Task Dialog */}
        <Dialog
          open={drawerOpen}
          onClose={closeTaskDrawer}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh'
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)', // More translucent backdrop
              backdropFilter: 'blur(2px)'
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {selectedTask ? 'Edit Task' : 'New Task'}
            </Typography>
              <IconButton onClick={closeTaskDrawer} size="small">
                <CloseIcon />
              </IconButton>
      </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={styles.patterns.formContainer}>
            <TextField
              fullWidth
              label="Task Title"
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Notes"
              value={taskForm.notes}
              onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })}
              multiline
              rows={3}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={taskForm.categoryId || ''}
                onChange={(e) => {
                  const cat = categories.find(c => (c._id || c.id) === e.target.value);
                  setTaskForm({
                    ...taskForm,
                    categoryId: e.target.value,
                    categoryName: cat?.name || ''
                  });
                }}
                label="Category"
              >
                {Array.isArray(categories) && categories.map(cat => (
                  <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DatePicker
              label="Due Date"
              value={taskForm.dueDate}
              onChange={(date) => setTaskForm({ ...taskForm, dueDate: date })}
              slotProps={{
                textField: { fullWidth: true }
              }}
            />

            <FormControl fullWidth>
              <Autocomplete
                multiple
                options={guests}
                getOptionLabel={(option) => option.name || ''}
                value={guests.filter(g =>
                  taskForm.assignees.some(a => {
                    const aid = getAssigneeGuestId(a);
                    return aid && (aid.toString() === (g._id || g.id)?.toString());
                  })
                )}
                onChange={(e, selectedGuests) => {
                  setTaskForm({
                    ...taskForm,
                    assignees: selectedGuests.map(guest => ({
                      guestId: guest._id,
                      displayName: guest.name
                    }))
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Assign To" placeholder="Select guests" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option._id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={taskForm.priority}
                onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Subtasks
            </Typography>
            {taskForm.subtasks.map((subtask, idx) => (
              <Box key={idx} sx={styles.layout.flexRow} gap={1} mb={1}>
                    <Checkbox
                  checked={subtask.completed}
                  onChange={(e) => updateSubtask(idx, 'completed', e.target.checked)}
                  size="small"
                />
                <TextField
                  fullWidth
                  size="small"
                  value={subtask.title}
                  onChange={(e) => updateSubtask(idx, 'title', e.target.value)}
                  placeholder="Subtask title"
                />
                <IconButton
                  size="small"
                  onClick={() => removeSubtask(idx)}
                >
                  <Trash size={18} />
                </IconButton>
    </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addSubtask}
            >
              Add Subtask
            </Button>

            </Box>
          </DialogContent>
          <DialogActions>
            {selectedTask && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  deleteTask(selectedTask._id);
                  closeTaskDrawer();
                }}
              >
                Delete
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              onClick={closeTaskDrawer}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveTask}
            >
              {selectedTask ? 'Update' : 'Create'} Task
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Management Modal */}
        <Dialog
          open={categoryModalOpen}
          onClose={() => setCategoryModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Manage Categories</Typography>
              <IconButton onClick={() => setCategoryModalOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
                </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {/* Add New Category */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="New Category Name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addCategory()}
                  placeholder="Enter category name"
                />
                <Button
                  variant="contained"
                  onClick={addCategory}
                  disabled={!newCategoryName.trim()}
                >
                  Add
                </Button>
              </Box>

              <Divider />

              {/* Existing Categories */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Existing Categories
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  {Array.isArray(categories) && categories.map(category => (
                    <Box
                      key={category._id || category.id}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {category.name}
                        </Typography>
                        {category.isDefault && (
                          <Chip
                            label="Default"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      {!category.isDefault && (
                  <IconButton
                          size="small"
                          onClick={() => deleteCategory(category._id || category.id)}
                          color="error"
                        >
                          <Trash size={18} />
                  </IconButton>
                      )}
                    </Box>
              ))}
      </Box>
    </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryModalOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default TodoList; 
